import {
  AuthUserPermission,
  CreateProgramEngagementParams,
  EqipApplicationResultsParams,
  MpxItemResponse,
  MpxResponseError,
  ProgramEngagement,
  ProgramEngagementConfirmTaskParams,
  ProgramEngagementLockDetailsError,
  ProgramEngagementModel,
  ProgramEngagementsAPI,
  ProgramEngagementsDetailParams,
  ProgramEngagementsQueryParams,
  ProgramEngagementUnconfirmTasksParams,
  UpdateProgramEngagementError,
  UpdateProgramEngagementParams,
} from '@cibo/core'
import { QUERY_KEY as LAND_MANAGER_QUERY_KEY, useInvalidateBatchedFields } from '@cibo/landmanager'
import { usePermissions } from '@cibo/profile'
import { logRequestError } from '@cibo/ui'
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query'
import { AxiosError, AxiosResponse } from 'axios'
import { PROGRAMS_QUERY_KEY } from './queryKey'

export const useProgramEngagements = (
  params?: ProgramEngagementsQueryParams,
  options?: { enabled?: boolean }
) => {
  const { can } = usePermissions()
  const hasSearchOptionAccess = can({ useAny: [AuthUserPermission.CROSS_ACCOUNT_ACCESS] })

  const finalParams: ProgramEngagementsQueryParams = hasSearchOptionAccess
    ? {
        ...params,
        searchOption: 'all' as const,
      }
    : params || {}

  // @todo: remove after https://cibotech.atlassian.net/browse/CPD-3767
  if (Array.isArray(finalParams.filters)) {
    for (let i = 0; i < finalParams.filters.length; i++) {
      if (finalParams.filters[i].userId) {
        if (typeof finalParams.filters[i].userId === 'number') {
          // @ts-ignore
          finalParams.filters[i].userId = finalParams.filters[i].userId.toString()
        } else if (Array.isArray(finalParams.filters[i].userId)) {
          // @ts-ignore
          finalParams.filters[i].userId = finalParams.filters[i].userId.map(id => id.toString())
        }
      }
    }
  }

  return useQuery({
    queryKey: [PROGRAMS_QUERY_KEY.PROGRAM_ENGAGEMENTS, finalParams, hasSearchOptionAccess],
    queryFn: async () => ProgramEngagementsAPI.list(finalParams).then(({ data }) => data),
    enabled: !!params,
    ...options,
  })
}

export const useProgramEngagement = (
  { id }: Partial<ProgramEngagementsDetailParams>,
  options?: Partial<Pick<UseQueryOptions, 'enabled'>>
) =>
  useQuery<ProgramEngagementModel, AxiosError>({
    queryKey: [PROGRAMS_QUERY_KEY.PROGRAM_ENGAGEMENTS, id],
    queryFn: () =>
      // id must be there since this gets disabled otherwise
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ProgramEngagementsAPI.detail({ id: id! }).then(({ data }) => {
        return new ProgramEngagementModel(data)
      }) as Promise<ProgramEngagementModel>,
    enabled: !!id,
    ...options,
  })

export const useCreateProgramEngagement = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: CreateProgramEngagementParams) =>
      ProgramEngagementsAPI.create(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROGRAMS_QUERY_KEY.PROGRAM_ENGAGEMENTS] })
    },
  })
}

export const useUpdateProgramEngagement = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (params: UpdateProgramEngagementParams) =>
      ProgramEngagementsAPI.update(params).then(({ data }) => {
        queryClient.setQueryData(
          [PROGRAMS_QUERY_KEY.PROGRAM_ENGAGEMENTS, params.id],
          new ProgramEngagementModel(data)
        )
        queryClient.invalidateQueries({
          queryKey: [PROGRAMS_QUERY_KEY.PROGRAM_ENGAGEMENTS],
          exact: true,
        })

        queryClient.invalidateQueries({
          queryKey: [LAND_MANAGER_QUERY_KEY.FIELDS_PAGINATED],
        })

        queryClient.invalidateQueries({ queryKey: [PROGRAMS_QUERY_KEY.TASK_DATA] })

        return new ProgramEngagementModel(data)
      }),
    onError: (error: AxiosError<UpdateProgramEngagementError>) => error,
  })
}

export const useDeleteProgramEngagement = () => {
  const queryClient = useQueryClient()
  return useMutation<
    AxiosResponse<MpxItemResponse<ProgramEngagement>>,
    AxiosError<MpxResponseError>,
    ProgramEngagementsDetailParams
  >({
    mutationFn: async ({ id }: ProgramEngagementsDetailParams) =>
      ProgramEngagementsAPI.delete({ id }).then(data => {
        queryClient.setQueryData([PROGRAMS_QUERY_KEY.PROGRAM_ENGAGEMENTS, id], null)
        queryClient.invalidateQueries({ queryKey: [PROGRAMS_QUERY_KEY.PROGRAM_ENGAGEMENTS] })
        return data
      }),
  })
}

export const useProgramEngagementConfirmTask = () => {
  const queryClient = useQueryClient()

  const invalidateBatchedFields = useInvalidateBatchedFields()

  return useMutation({
    mutationKey: ['programEngagementConfirmTask'],
    mutationFn: async (params: ProgramEngagementConfirmTaskParams) =>
      ProgramEngagementsAPI.confirmTask(params).then(({ data }) => {
        const updatedEngagementModel = new ProgramEngagementModel(data)

        queryClient.setQueryData(
          [PROGRAMS_QUERY_KEY.PROGRAM_ENGAGEMENTS, params.id],
          updatedEngagementModel
        )

        queryClient.invalidateQueries({
          queryKey: [PROGRAMS_QUERY_KEY.TASK_DATA],
        })
        queryClient.invalidateQueries({
          queryKey: [PROGRAMS_QUERY_KEY.PROGRAM_ENGAGEMENT_DETAILS, params.id],
          exact: true,
        })

        invalidateBatchedFields(data.fields)

        return updatedEngagementModel
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [LAND_MANAGER_QUERY_KEY.FIELDS_PAGINATED],
      })
      queryClient.invalidateQueries({
        queryKey: [PROGRAMS_QUERY_KEY.TASK_DATA],
      })
    },
    onError: (error: AxiosError<ProgramEngagementLockDetailsError>, variables, context) => {
      logRequestError(error, { query: 'useProgramEngagementConfirmTask', variables, context })
      return error
    },
  })
}

export const useProgramEngagementUnconfirmTasks = () => {
  const queryClient = useQueryClient()

  const invalidateBatchedFields = useInvalidateBatchedFields()

  return useMutation({
    mutationFn: async (params: ProgramEngagementUnconfirmTasksParams) =>
      ProgramEngagementsAPI.unconfirmTasks(params).then(({ data }) => {
        queryClient.setQueryData(
          [PROGRAMS_QUERY_KEY.PROGRAM_ENGAGEMENTS, params.id],
          new ProgramEngagementModel(data)
        )

        queryClient.invalidateQueries({
          queryKey: [PROGRAMS_QUERY_KEY.TASK_DATA],
        })
        queryClient.invalidateQueries({
          queryKey: [PROGRAMS_QUERY_KEY.PROGRAM_ENGAGEMENT_DETAILS, params.id],
          exact: true,
        })

        invalidateBatchedFields(data.fields)

        return data
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROGRAMS_QUERY_KEY.PROGRAM_ENGAGEMENTS] })
    },
    onError: (error: AxiosError<ProgramEngagementLockDetailsError>) => error,
  })
}

export const useUploadEqipApplicationResults = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (params: EqipApplicationResultsParams) =>
      ProgramEngagementsAPI.uploadApplicationResults(params).then(({ data }) => {
        queryClient.invalidateQueries({
          queryKey: [PROGRAMS_QUERY_KEY.PROGRAM_ENGAGEMENT_DETAILS, params.id],
        })
        queryClient.invalidateQueries({
          queryKey: [PROGRAMS_QUERY_KEY.PROGRAM_ENGAGEMENTS, params.id],
        })
        return data
      }),
  })
}

export const useProgramEngagementInvalidation = () => {
  const queryClient = useQueryClient()
  return {
    invalidateAllEngagements: () =>
      queryClient.invalidateQueries({ queryKey: [PROGRAMS_QUERY_KEY.PROGRAM_ENGAGEMENTS] }),
  }
}
